import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { queryKeys, reportStorageUrl } from '@/http/constants';
import fetchWrapper from '@/http/fetchWrapper';
import type { AuditData } from '@eye-able/types';
import {
  useDomainOptions,
  type DomainOption,
} from '@/components/FormDomainSelect';
import { useFetchAuditEntriesByIds } from '@/http/queries/useFetchAuditEntriesLookup';
import type { PolicyResponse } from '@/http/queries/useFetchPolicies';

export type PolicyDomainOption = DomainOption & { lastCompletedAudit?: string };

export function latestCompletedAudit(
  history: Record<string, unknown> | undefined,
) {
  return Object.keys(history ?? {})
    .filter((date) => Number.isFinite(Date.parse(date)))
    .sort((first, second) => Date.parse(second) - Date.parse(first))[0];
}

export function normalizeAllowedTime(value: string | undefined) {
  const match = value?.match(
    /^(?:\d{4}-\d{2}-\d{2}T)?([0-2]\d:[0-5]\d:[0-5]\d)(?:\.\d+Z?|Z)?$/,
  );
  return match?.[1];
}

export function getPolicyAuditEntryIds(policies: PolicyResponse[]): number[] {
  return Array.from(
    new Set(policies.flatMap((policy) => policy.auditEntryIds)),
  ).sort((firstId, secondId) => firstId - secondId);
}

export function mergeRefreshedDomainOptions(
  domainOptions: DomainOption[],
  refreshedAuditEntries: AuditData[],
): DomainOption[] {
  const refreshedEntriesById = new Map(
    refreshedAuditEntries.map((auditEntry) => [auditEntry.id, auditEntry]),
  );

  return domainOptions.map((domainOption) => {
    const refreshedAuditEntry = refreshedEntriesById.get(domainOption.id);
    if (!refreshedAuditEntry) return domainOption;

    return {
      ...domainOption,
      label: refreshedAuditEntry.domainAlias || refreshedAuditEntry.domain,
      domain: refreshedAuditEntry.domain,
      domainAlias: refreshedAuditEntry.domainAlias || undefined,
      searchText: refreshedAuditEntry.domainAlias
        ? `${refreshedAuditEntry.domainAlias} ${refreshedAuditEntry.domain}`
        : refreshedAuditEntry.domain,
      auditEntry: {
        ...refreshedAuditEntry,
        allowedTime: normalizeAllowedTime(refreshedAuditEntry.allowedTime),
      },
    };
  });
}

/** Domain options backed by a fresh lookup instead of the long-lived licence cache. */
export function usePolicyDomainOptions(
  policies: PolicyResponse[],
): PolicyDomainOption[] {
  const auditEntryIds = useMemo(
    () => getPolicyAuditEntryIds(policies),
    [policies],
  );
  const domainOptions = useDomainOptions(auditEntryIds);
  const { data: refreshedAuditEntries = [] } = useFetchAuditEntriesByIds(
    auditEntryIds,
    true,
  );

  const refreshedOptions = useMemo(
    () => mergeRefreshedDomainOptions(domainOptions, refreshedAuditEntries),
    [domainOptions, refreshedAuditEntries],
  );
  const referencedOptions = refreshedOptions.filter((option) =>
    auditEntryIds.includes(option.id),
  );
  const histories = useQueries({
    queries: referencedOptions.map((option) => {
      const directory = option.auditEntry?.cdnDirectory;
      const path = directory
        ? reportStorageUrl + directory + (directory.endsWith('/') ? '' : '/')
        : null;
      return {
        queryKey: [queryKeys.HISTORY, path],
        enabled: Boolean(path),
        retryOnMount: false,
        queryFn: async () => {
          if (import.meta.env.VITE_OFFLINE_MODE === 'true') {
            return (await import('../../../../mockData/history.json')).default;
          }
          return fetchWrapper.get<Record<string, unknown>>(
            `${path}history.json`,
          );
        },
      };
    }),
  });
  return referencedOptions.map((option, index) => ({
    ...option,
    auditEntry: option.auditEntry
      ? {
          ...option.auditEntry,
          allowedTime: normalizeAllowedTime(option.auditEntry.allowedTime),
        }
      : undefined,
    lastCompletedAudit: latestCompletedAudit(histories[index].data),
  }));
}
